import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { fabric } from "fabric-pure-browser";
import { decompress } from "compress-json";
import WebFont from "webfontloader";
import snakeCase from "lodash/snakeCase";
import { dateFomat } from "../utils/dateFormat";
import { FABRIC_DIMENSIONS, getObjectValue } from "@cti-workspace/helpers";
import { Button } from "@cti-workspace/ui";
import axios from "axios";
import { ArrowsOut, CaretLeft } from "@phosphor-icons/react";
import { ContrastColor } from "../VerifierPageComponents/Button";

const ST = require("selecttransform").SelectTransform;

const st = new ST();
const customFonts = ["Arial", "Arial Black"];

const isMobileView = () => window.screen.width <= 768;

const FabricCert = forwardRef(
	(
		{
			document,
			locale,
			config: { credDimension, data },
			isFullScreen,
			onClose = () => {},
			onZoom = () => {},
			setCertBgImg = () => {},
			setFabricPageNames = () => {},
			activePageName = null,
			setPrintPageSize = () => {},
			setShowSimple,
			enqueueSnackbar,
			fabricCertiFullPageRef,
			downloadPdf = () => {},
      theme = {},
      fallbackBackgroundImg = null,
		},
		ref
  ) => {
		const canvasRef = useRef(null);
		const [loading, setLoading] = useState(true);
		const [fabricData, setFabricData] = useState(null);
		const [activePage, setActivePage] = useState(null);
		const snackbarInitiated = useRef(false);
		const dialogRef = useRef(null);

		const loadFromJSON = useCallback(async (json) => {
			try {
				(function () {
					var defaultOnTouchStartHandler = fabric.Canvas.prototype._onTouchStart;
					fabric.util.object.extend(fabric.Canvas.prototype, {
						_onTouchStart: function (e) {
							var target = this.findTarget(e);
							// Touch scrolling is for mobile devices
							// if allowTouchScrolling is enabled, canvas not created
							// the touch position and in drawing mode,
							// need to skip touchevent by fabric canvas d
							if (this.allowTouchScrolling && !target && !this.isDrawingMode) {
								return;
							}

							// call the default behavior if above condition not satisfied
							defaultOnTouchStartHandler.call(this, e);
						},
					});
				})();
				let canvasElem = new fabric.Canvas(canvasRef.current, {
					preserveObjectStacking: true,
					defaultCursor: "default",
					backgroundColor: "#f3f3f3",
					enableRetinaScaling: true,
					allowTouchScrolling: true,
				});

				let zoomFactor = screen.width <= 576 ? 0.4 : screen.width <= 768 ? 0.75 : screen.width <= 992 ? 1.05 : screen.width <= 1200 ? 1.05 : 0.57;
				let parentElementDimension = {
					width: "",
					height: "",
				};
				if (isFullScreen) {
					const parentElement = window.document.getElementById("zoomed-certificate");
					parentElementDimension = {
						width: parentElement?.clientWidth,
						height: parentElement?.clientHeight,
					};
				}

				const fullScreenZoomFactor = parentElementDimension.width <= 576 ? 0.46 : parentElementDimension.width <= 768 ? 0.75 : parentElementDimension.width <= 992 ? 1.05 : parentElementDimension.width <= 1200 ? 1.05 : 1.2;
				zoomFactor = isFullScreen ? fullScreenZoomFactor : zoomFactor;
				canvasElem.loadFromJSON(
					json,
					() => {
						try {
							let width = 640;
							let height = 437;
							if (json.credDimension) {
								const dimensions = json.credDimension.split("X");
								width = dimensions[0];
								height = dimensions[1];
							} else if (credDimension) {
								const dimensions = credDimension.split("X");
								width = dimensions[0];
								height = dimensions[1];
							}

							const zoomedHeight = height * zoomFactor;
							if ((Number(height) > Number(width) || zoomedHeight >= window.innerHeight - 100) && isFullScreen) {
								zoomFactor = window.innerHeight / Number(height) - 0.15;
							}

							let isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
							if (isIOS) {
								// console.log("This is a IOS device");
								canvasElem.setWidth(width * zoomFactor);
								canvasElem.setHeight(height * zoomFactor);
								canvasElem.setZoom(zoomFactor);
							} else {
								// console.log("This is not a IOS device");
								canvasElem.setWidth(width * 2);
								canvasElem.setHeight(height * 2);
								canvasElem.setZoom(2);
							}

							canvasElem.renderAll();

							if (isFullScreen) {
								[...window.document?.getElementById("zoomed-certificate")?.getElementsByClassName("canvas-container")]?.forEach((value) => {
									value.style.width = width * zoomFactor + "px";
									value.style.height = height * zoomFactor + "px";
								});
							} else {
								[...window.document?.getElementsByClassName("canvas-container")]?.forEach((value) => {
									value.style.width = width * zoomFactor + "px";
									value.style.height = height * zoomFactor + "px";
								});
							}
							canvasElem.contextContainer.canvas.style.width = width * zoomFactor + "px";
							canvasElem.contextContainer.canvas.style.height = height * zoomFactor + "px";
							canvasElem.contextTop.canvas.style.width = width * zoomFactor + "px";
							canvasElem.contextTop.canvas.style.height = height * zoomFactor + "px";
							setLoading(false);
						} catch (err) {}
					},
          (o, object) => {
            if (object) {
              object.set("selectable", false);
            }
          }
				);
				if (isFullScreen) fabricCertiFullPageRef.current = canvasElem;
			} catch (error) {
				setShowSimple(true);
				if (!snackbarInitiated.current) {
					snackbarInitiated.current = true;
					enqueueSnackbar("WARNING: Due to an error we are not able display your credential. please contact support.", { variant: "warning" });
				}
				console.error(error);
			}
		}, []);

    const uploadData = async (jsonData, dimensions) => {
			try {
				if (jsonData) {
					let val = {};
					if (document.transcript && document.transcript.length) {
						document.transcript.map((item) => {
							return (val = {
								...val,
								[`${snakeCase(item.name)}${item.name?.split(" ").length > 1 ? "" : "_"}`]: item.grade,
							});
						});
					}
					const issuanceDate = document.issuanceDate || document.issuedOn;

					const replaceGuide = {
						Issued_to_Name: document.recipient.name,
						Recipient_Name: document.recipient.name,
						Issuer_Name: document.recipient.name,
						Recipient_First_Name: document.recipient.firstName,
						Recipient_Last_Name: document.recipient.lastName,
						Credential_Name: document.name,
						Issued_date: dateFomat({ date: issuanceDate, locale }),
						Public_Url: document?.additionalDataMain?.shortURLs?.publicUrl || window?.location?.href || "",
						...val,
					};
					let allObjects = [];
					// as if condition already checks for jsonData from being undefined, disabling the eslint rule here.
          // eslint-disable-next-line no-unsafe-optional-chaining
          if (typeof jsonData.backgroundImage === "object" && jsonData.backgroundImage.src.startsWith("http") ) {
            var bgImg;
            try {
              bgImg = await axios.get(jsonData.backgroundImage.src + `?timestamp=${new Date()}`, {
                headers: {
                  "Content-Type": "application/octet-stream",
                },
                responseType: "blob",
                withCredentials: false,
              });
            } catch (e) {
              let bgImgDimensions = [];
              await new Promise((resolve) => {
                if (fabric.Image && fabric.Image !== undefined) {
                  fabric.Image.fromURL(
                    fallbackBackgroundImg + `?tstamp=${new Date()}`,
                    function (Img) {
                      bgImgDimensions[0] = Img.width;
                      bgImgDimensions[1] = Img.height;
                      console.log("bgImgDimensions set");
                      return resolve(null);
                    }, {
                    crossOrigin: "Annoymous",
                    withCredentials: false,
                  }
                  );
                }
              });

              bgImg = await axios.get(fallbackBackgroundImg + `?timestamp=${new Date()}`, {
                headers: {
                  "Content-Type": "application/octet-stream",
                },
                responseType: "blob",
                withCredentials: false,
              });

              if (bgImgDimensions.length > 0) {
                // Rescale the backgroundImage object to fit the new image
                const scaleWidth = jsonData.backgroundImage.width * jsonData.backgroundImage.scaleX;
                const scaleHeight = jsonData.backgroundImage.height * jsonData.backgroundImage.scaleY;

                const scaleX = bgImgDimensions[0] > 0 ? scaleWidth / bgImgDimensions[0] : 1;
                const scaleY = bgImgDimensions[1] > 0 ? scaleHeight / bgImgDimensions[1] : 1;
                // let scale = Math.max(scaleX, scaleY);
                jsonData.backgroundImage.scaleX = scaleX;
                jsonData.backgroundImage.scaleY = scaleY
              } else {
                console.log("bgImgDimensions is empty");
              }
            }
            //if (isFullScreen) {             
            const { data } = bgImg;
            jsonData.backgroundImage.src = window.URL.createObjectURL(data);
            //}
          }

          for (let value of jsonData?.objects) {
						if (value.type === "group" && value.objects.length === 2 && value.objects[0].type === "image" && value.objects[1].type === "textbox") {
							const replaceValue = value.objects[1].text.replace("{{", "").replace("}}", "");
							const { [replaceValue]: imageSrc } = replaceGuide;
							const imageValue = await new Promise((resolve) => {
                fabric.Image.fromURL(imageSrc, async function (img) {
                  if (img == null) {
                    /*console.log("Fabric. " + imageSrc);
                    console.log(`Fabric: ${JSON.stringify(document, null, 1)}`);
                    console.log(`Fabric : ${JSON.stringify(replaceGuide, null, 1)}`);
                    if (document.backgroundImg) {
                      await new Promise((resolve) => {
                        fabric.Image.fromURL(document.backgroundImg, function (img2) {
                          img = img2;
                        });
                      });
                    }
                    if (img == null) { */
                      return resolve(null);
                    // }
                  }
									const scaleWidth = value.width * value.scaleX;
									const scaleHeight = value.height * value.scaleY;

									const scaleX = img.width > 0 ? scaleWidth / img.width : 1;
									const scaleY = img.height > 0 ? scaleHeight / img.height : 1;
									let scale = Math.min(scaleX, scaleY);
									img.set({ left: value.left, top: value.top, scaleX: scale, scaleY: scale });
									img.toObject = (function (toObject) {
                    return function () {
                      try {
                        console.log("fabric.util.object");
                        return fabric.util.object.extend(toObject.call(this), {
                          scaleX: this.scaleX,
                          scaleY: this.scaleY,
                          crossOrigin: "anonymous",
                        });
                      } catch (e) {
                        console.log("fabric.util.object null");
                        return null;
                      }
										};
									})(img.toObject);
									resolve(img.toJSON());
								});
							});
							allObjects.push({
								...imageValue,
							});
						} else {
							if (value.type === "image" && isFullScreen) {
								const { data } = await axios.get(value.src + `?timestamp=${new Date()}`, {
									headers: {
										"Content-Type": "application/octet-stream",
									},
									responseType: "blob",
									withCredentials: false,
								});
								value.src = window.URL.createObjectURL(data);
							}
							allObjects.push(value);
						}
					}
					jsonData.objects = allObjects;
					const certDisplayJSON = st.transformSync(jsonData, replaceGuide);

					loadFromJSON(certDisplayJSON);
				}
			} catch (error) {
				setShowSimple(true);
				if (!snackbarInitiated.current) {
					snackbarInitiated.current = true;
					enqueueSnackbar("WARNING: Due to an error we are not able display your credential. Please contact support.", { variant: "warning" });
				}
				console.error(error);
			}
		};

		const loadFabricPage = (jsonData) => {
			try {
				let fonts = [];
				let dimensions = [];
				const jsonCredDimension = jsonData?.credDimension;

				if (jsonCredDimension) {
					dimensions = jsonCredDimension.split("X");
				} else if (credDimension) {
					dimensions = credDimension.split("X");
				}

				let pageType = "A4";
				if (dimensions?.length === 2) {
					const dimensionRatio = Number((Number(dimensions[0]) / Number(dimensions[1])).toFixed(2));
					if (Number(dimensions[0]) < Number(dimensions[1])) {
						if (dimensionRatio <= FABRIC_DIMENSIONS.A4_PORTRAIT.toFixed(2) || dimensionRatio <= FABRIC_DIMENSIONS.A4_PORTRAIT_OLD.toFixed(2)) {
							pageType = "A4";
						} else {
							pageType = "letter";
						}
					} else {
						if (dimensionRatio >= FABRIC_DIMENSIONS.A4_LANDSCAPE.toFixed(2) || dimensionRatio >= FABRIC_DIMENSIONS.A4_LANDSCAPE_OLD.toFixed(2)) {
							pageType = "A4";
						} else {
							pageType = "letter";
						}
					}
					if (Number(dimensions[0]) > Number(dimensions[1])) {
						setPrintPageSize({ type: "fabric", size: "landscape", pageType });
					} else {
						setPrintPageSize({ type: "fabric", size: "portrait", pageType });
					}
        } else {
          if (window.location.host.includes("hrpa.ca")) {
            setPrintPageSize({ type: "fabric", size: "landscape", pageType: "A4" });
          } else {
            if (!snackbarInitiated.current) {
              snackbarInitiated.current = true;
              enqueueSnackbar("WARNING: Credential dimension was not found, default is set to US letter landscape. If these dimensions are wrong please contact support.", { variant: "warning" });
            }
            setPrintPageSize({ type: "fabric", size: "landscape", pageType: "letter" });
          }
				}

				jsonData.objects.forEach((element) => {
					if (element.fontFamily) {
						fonts.push(element.fontFamily);
					}
				});
				if (fonts.length > 0) {
					fonts = [...new Set(fonts)];
					const newFonts = fonts.filter((item) => !customFonts.includes(item));

					if (newFonts.length) {
						WebFont.load({
							google: {
								families: newFonts,
							},
              active: () => uploadData(jsonData, dimensions),
						});
					} else {
            uploadData(jsonData, dimensions);
					}
				} else {
          uploadData(jsonData, dimensions);
				}
			} catch (error) {
				setShowSimple(true);
				if (!snackbarInitiated.current) {
					snackbarInitiated.current = true;
					enqueueSnackbar("WARNING: Due to some error we are not able display your credential. please contact support.", { variant: "warning" });
				}
				console.error(error);
			}
		};

		useEffect(() => {
			try {
        const decompressJsonData = decompress(data);
        console.log(decompressJsonData);
				const jsonData = decompressJsonData.isMultiPage ? getObjectValue(decompressJsonData?.payload, activePageName || decompressJsonData?.payload?.defaultPage) : decompressJsonData;
        loadFabricPage(jsonData);
        if (jsonData === decompressJsonData) {
          console.log("jsonData === decompressJsonData");
        }
        console.log(jsonData);
				const bgUrl = getObjectValue(jsonData, "backgroundImage.src", "");
				setCertBgImg(bgUrl);
				if (decompressJsonData.isMultiPage) {
					setFabricPageNames(Object.keys(decompressJsonData?.payload).filter((value) => value !== "defaultPage"));
					setActivePage(decompressJsonData?.payload?.defaultPage);
				}
				setFabricData(decompressJsonData);
			} catch (error) {
				setShowSimple(true);
				if (!snackbarInitiated.current) {
					snackbarInitiated.current = true;
					enqueueSnackbar("WARNING: Due to some internal issue we are not able load your credential. please contact support.", { variant: "warning" });
				}
				console.error(error);
			}
		}, []);

		useEffect(() => {
			try {
				if (activePageName && activePageName !== "defaultPage") {
					onPageChange(activePageName);
				}
			} catch (error) {
				setShowSimple(true);
				if (!snackbarInitiated.current) {
					snackbarInitiated.current = true;
					enqueueSnackbar("WARNING: Due to some error we are not able display your credential. Please contact support.", { variant: "warning" });
				}
				console.error(error);
			}
		}, [activePageName, !!fabricData]);

		const onPageChange = (e) => {
			const jsonData = getObjectValue(fabricData?.payload, e);
			if (jsonData) {
				const bgUrl = getObjectValue(jsonData, "backgroundImage.src", "");
				setCertBgImg(bgUrl);
				setActivePage(e);
				loadFabricPage(jsonData);
			}
		};

		const handleMouseDown = (e) => {
			if (isFullScreen && dialogRef.current && !dialogRef.current.contains(e.target)) {
				e?.stopPropagation();
				onClose(e);
			}
		};

		return (
			<div style={{ display: "flex", justifyContent: "center", flexDirection: "column", alignItems: "center" }} onMouseDown={handleMouseDown}>
				{/* {fabricData?.isMultiPage ? (
				<select style={{ width: 200 }} name="fontStyle" className="btn-object-action" onChange={onPageChange} value={activePage}>
					{fabricData?.payload
						? Object.keys(fabricData?.payload)
							.filter((value) => value !== "defaultPage")
							.map((value, i) => (
								<option key={i} value={value}>
									{value}
								</option>
							))
						: null}
				</select>
			) : null} */}
				<div
					style={{
						position: "relative",
						overflow: isFullScreen ? "unset" : "hidden",
						borderRadius: isFullScreen ? 0 : "10px",
						paddingInline: "10px",
						cursor: "pointer",
					}}
					onClick={(e) => {
						e?.stopPropagation();
						onZoom();
					}}
					id="verifier-cert-container">
					{isFullScreen ? (
						<span style={{ position: "absolute", zIndex: 1, right: 16, top: 8 }}>
							<Button
								buttonType="icon"
								color={"white"}
								icon="cancel-circle"
								iconStyle={{ weight: "fill", color: "#C0C0C0" }}
								noBorder={true}
								borderRadius={"50%"}
								buttonStyle={{ padding: 0, backgroundColor: "transparent" }}
								iconSize="2rem"
								onClick={(e) => {
									e?.stopPropagation();
									onClose();
								}}
							/>
						</span>
					) : (
						<div id="verifier-cert-fullscreen-btn" style={theme?.primaryColor ? { backgroundColor: theme?.primaryColor } : {}}>
							<CaretLeft color={theme?.primaryColor ? ContrastColor(theme?.primaryColor) : "white"} />
							<ArrowsOut color={theme?.primaryColor ? ContrastColor(theme?.primaryColor) : "white"} />
						</div>
					)}
					{isFullScreen && !isMobileView() ? (
						<span style={{ position: "absolute", zIndex: 1, right: "50%", bottom: -55, transform: "translatex(50%)" }}>
							<Button
								buttonType="icon"
								color={"white"}
								icon="download"
								iconStyle={{ weight: "fill", color: "#0E658F" }}
								noBorder={true}
								borderRadius={"5px"}
								buttonStyle={{ padding: "5px 50px", backgroundColor: "white" }}
								iconSize="1.5rem"
								mainDivContainerProps={{
									id: "verifierPdfDownloadButton",
									onMouseDown: (e) => {
										e?.stopPropagation();
									},
								}}
								onClick={(e) => {
									e?.stopPropagation();
									downloadPdf();
								}}
							/>
						</span>
					) : (
						""
					)}
					<div ref={dialogRef}>
						<div
							ref={ref}
							style={{
								height: "auto",
								textAlign: "center",
								margin: "auto",
								pointerEvents: "none",
								backgroundColor: "gray",
							}}>
							<canvas className="fabric-cert" ref={canvasRef} alt="certificates" />
							{loading && <div> loading.. </div>}
						</div>
					</div>
				</div>
			</div>
		);
	}
);

export default FabricCert;
